import { CheckCircle2, Save, SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import EmptyState from "../../components/EmptyState.jsx";
import { useToast } from "../../context/ToastContext.jsx";
import { sectionKeyFromRecord } from "../../utils/userPreferences.js";

function buildSectionGroups(routines = []) {
  const map = new Map();
  routines.forEach((routine) => {
    const key = sectionKeyFromRecord(routine);
    if (!key.trim()) return;
    if (!map.has(routine.classId || routine.className || "Class")) {
      map.set(routine.classId || routine.className || "Class", {
        className: routine.className || "Class",
        sections: new Map()
      });
    }
    const group = map.get(routine.classId || routine.className || "Class");
    if (!group.sections.has(key)) {
      group.sections.set(key, {
        key,
        label: routine.sectionName || "Section",
        room: routine.roomNumber || "N/A"
      });
    }
  });

  return [...map.values()]
    .sort((a, b) => a.className.localeCompare(b.className, undefined, { numeric: true }))
    .map((group) => ({
      ...group,
      sections: [...group.sections.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
    }));
}

export default function UserSettings() {
  const { allRoutines, sectionFilter, setSectionFilter } = useOutletContext();
  const { showToast } = useToast();
  const [selected, setSelected] = useState(sectionFilter || []);
  const groups = useMemo(() => buildSectionGroups(allRoutines), [allRoutines]);
  const allKeys = useMemo(() => groups.flatMap((group) => group.sections.map((section) => section.key)), [groups]);
  const isAll = selected.length === 0 || selected.length === allKeys.length;

  function toggle(key) {
    setSelected((current) => (current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));
  }

  function save() {
    const nextValue = selected.length === allKeys.length ? [] : selected;
    setSectionFilter(nextValue);
    showToast(nextValue.length ? "Section filter saved" : "All sections enabled");
  }

  return (
    <section className="user-settings-page">
      <div className="page-hero compact">
        <p>Personal filter</p>
        <h2>My Sections</h2>
      </div>

      <article className="card user-filter-card">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Visible routines</p>
            <h3>{isAll ? "All sections selected" : `${selected.length} section${selected.length === 1 ? "" : "s"} selected`}</h3>
          </div>
          <SlidersHorizontal size={22} />
        </div>
        <p className="muted-copy">Only selected sections will show on Home, Routine and Pending OUT. Select All brings the app back to normal full view.</p>
        <div className="form-actions user-filter-actions">
          <button className="btn btn-ghost" type="button" onClick={() => setSelected([])}>
            <CheckCircle2 size={17} />
            Select All
          </button>
          <button className="btn btn-primary" type="button" onClick={save}>
            <Save size={17} />
            Save Filter
          </button>
        </div>
      </article>

      {groups.length ? (
        <div className="section-filter-list">
          {groups.map((group) => (
            <article className="card section-filter-card" key={group.className}>
              <h3>{group.className}</h3>
              <div className="section-filter-options">
                {group.sections.map((section) => {
                  const checked = selected.length === 0 || selected.includes(section.key);
                  return (
                    <label className={checked ? "section-filter-option selected" : "section-filter-option"} key={section.key}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          if (selected.length === 0) {
                            setSelected(allKeys.filter((key) => key !== section.key));
                            return;
                          }
                          toggle(section.key);
                        }}
                      />
                      <span>{section.label}</span>
                      <small>Room {section.room}</small>
                    </label>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState icon="⚙️" title="No sections found today" text="Sections will appear here when today’s routine is loaded." />
      )}
    </section>
  );
}
